import Icon from 'Components/Base/Icon'
import Typography from 'Components/Base/Typography'
import dayjs from 'dayjs'
import { useAppSelector } from 'hooks/hooks'
import React, { useEffect, useState } from 'react'
import { getScaleReadingsSummary } from 'reducers/dashboardSlice'
import { useTheme } from 'styled-components'
import { ChartReading } from 'types/ChartReading'
import * as Victory from 'victory'
import '../../DashboardScalePage.scss'

type Props = {
  chartReadings: ChartReading[] | undefined
  isMobile: boolean
  chartHeight: number
  hasBodyComposition: boolean
  fatReadings: ChartReading[] | undefined
  muscleReadings: ChartReading[] | undefined
  waterReadings: ChartReading[] | undefined
}

export default function LineChart({ chartReadings, chartHeight, isMobile, hasBodyComposition, fatReadings, muscleReadings, waterReadings }: Props) {
  const theme = useTheme()
  const yAxisMax = chartReadings && chartReadings.length > 0 ? Math.max(...chartReadings.map((reading) => reading.value)) + 10 : 250
  const yAxisMin = chartReadings && chartReadings.length > 0 ? Math.min(...chartReadings.map((reading) => reading.value)) - 10 : 0
  const [data, setData] = useState<ChartReading[]>()
  const [selectedTab, setSelectedTab] = useState(0) //weight = 0; Body Comp = 1; Fat%= 2; Muscle%=3; Water%=4;
  const scaleReadingsSummary = useAppSelector(getScaleReadingsSummary)

  useEffect(() => {
    switch (selectedTab) {
      case 0:
        return setData(chartReadings)
      case 1:
        return setData(chartReadings)
      case 2:
        return setData(fatReadings)
      case 3:
        return setData(muscleReadings)
      case 4:
        return setData(waterReadings)
      default:
        return setData(chartReadings)
    }
  }, [selectedTab, chartReadings, waterReadings, fatReadings, muscleReadings])

  const gradientColor = () => {
    switch (selectedTab) {
      case 0:
        return theme.colors.primary1
      case 1:
        return theme.colors.charts.inRange
      case 2:
        return '#253746'
      case 3:
        return theme.colors.charts.inRange
      case 4:
        return '#0092BC'
      default:
        return theme.colors.primary1
    }
  }

  const orderedMuscleDates = muscleReadings?.map((m) => m.date)
  const orderedFatDates = fatReadings?.map((m) => m.date)
  const orderedWaterDates = waterReadings?.map((m) => m.date)
  return (
    <div className="scale-line-chart-card">
      {hasBodyComposition && (
        <div className="button-bar">
          <div className="button-group" style={{ '--primary': theme.colors.primary1 } as React.CSSProperties}>
            <button className={selectedTab === 0 ? 'active' : ''} onClick={() => setSelectedTab(0)}>
              <Typography>Weight</Typography>
            </button>
            <button className={selectedTab !== 0 ? 'active' : ''} onClick={() => setSelectedTab(1)}>
              <Typography>Body Composition</Typography>
            </button>
          </div>
          {selectedTab !== 0 && (
            <div className="button-group" style={{ '--primary': theme.colors.primary1 } as React.CSSProperties}>
              <button
                className={selectedTab === 1 ? 'active' : ''}
                onClick={() => setSelectedTab(1)}
                style={{ padding: '10px 30px', display: 'flex', flexDirection: 'row' }}
              >
                <Typography size={isMobile ? 12 : 14}>All</Typography>
              </button>
              <button
                className={selectedTab === 2 ? 'active' : ''}
                onClick={() => setSelectedTab(2)}
                style={{ padding: '10px 10px', display: 'flex', flexDirection: 'row' }}
              >
                <Icon
                  name="solidCircle"
                  size={isMobile ? '2xs' : 'sm'}
                  styles={{ marginRight: 5, marginTop: 2.5 }}
                  color={selectedTab !== 2 ? '#253746' : '#fff'}
                />
                <Typography size={isMobile ? 12 : 14}>Fat %</Typography>
              </button>
              <button
                className={selectedTab === 3 ? 'active' : ''}
                onClick={() => setSelectedTab(3)}
                style={{ padding: '10px 10px', display: 'flex', flexDirection: 'row' }}
              >
                <Icon
                  name="square"
                  size={isMobile ? '2xs' : 'sm'}
                  styles={{ marginRight: 5, marginTop: 2.5 }}
                  color={selectedTab !== 3 ? theme.colors.charts.inRange : '#fff'}
                />
                <Typography size={isMobile ? 12 : 14}>Muscle %</Typography>
              </button>
              <button
                className={selectedTab === 4 ? 'active' : ''}
                onClick={() => setSelectedTab(4)}
                style={{ padding: '10px 10px', display: 'flex', flexDirection: 'row' }}
              >
                <Icon
                  name="triangle"
                  size={isMobile ? '2xs' : 'sm'}
                  styles={{ marginRight: 5, marginTop: 2.5 }}
                  color={selectedTab !== 4 ? '#0092BC' : '#fff'}
                />
                <Typography size={isMobile ? 12 : 14}>Water %</Typography>
              </button>
            </div>
          )}
        </div>
      )}
      {chartReadings && chartReadings.length <= 0 && (
        <Typography font="semi-bold" styles={{ position: 'absolute', top: '50%', left: '50%' }}>
          No Data
        </Typography>
      )}
      <div style={{ backgroundColor: 'white', marginLeft: 20, height: 400 }}>
        {/* Wrap in an SVG and make VictoryChart non-standalone */}
        <svg width={0.9 * window.innerWidth} height={350} style={{ display: 'block' }}>
          {/* 1) Define the gradient here (in the same <svg> as the chart) */}
          <defs>
            <linearGradient id="lineAreaGradient" x1="0" y1="0" x2="0" y2="1">
              {/* Near the line */}
              <stop offset="0%" stopColor={gradientColor()} stopOpacity="0" />
              {/* Toward the x-axis */}
              <stop offset="100%" stopColor={gradientColor()} stopOpacity="0.55" />
            </linearGradient>
          </defs>

          <Victory.VictoryChart
            standalone={false} // IMPORTANT
            padding={{
              top: isMobile ? 50 : 70,
              bottom: isMobile ? 50 : 40,
              right: 30,
              left: 60,
            }}
            width={0.85 * window.innerWidth}
            height={350}
            style={{ parent: { touchAction: 'manipulation' } }}
            maxDomain={selectedTab !== 0 ? { y: 100 } : chartReadings ? { y: yAxisMax } : { y: 350 }}
            minDomain={selectedTab !== 0 ? { y: 0 } : chartReadings ? { y: yAxisMin } : { y: 0 }}
            containerComponent={<Victory.VictoryVoronoiContainer />}
          >
            <Victory.VictoryAxis
              tickCount={5}
              width={20}
              tickFormat={(tick) => dayjs(tick).format('M/DD')}
              style={{
                grid: {
                  stroke: ({ tick }) => (tick === -10 ? 'transparent' : '#ECECEC'),
                  strokeWidth: 1,
                },
                axis: { stroke: 'transparent' },
                tickLabels: { fill: '#000', fontWeight: 500, fontSize: 14 },
              }}
            />
            {selectedTab === 0 ? (
              <Victory.VictoryAxis
                dependentAxis
                label="Weight (lbs)"
                tickCount={6}
                crossAxis={false}
                style={{
                  grid: {
                    stroke: ({ tick }) => (tick === -10 ? 'transparent' : '#ECECEC'),
                    strokeWidth: 1,
                  },
                  axis: { stroke: '#ECECEC', strokeWidth: 0 },
                  axisLabel: { fontSize: 20, padding: 40, fill: '#000', fontFamily: 'Raleway', fontWeight: 500 },
                  ticks: { strokeWidth: 0 },
                  tickLabels: { fill: '#000', fontWeight: 500, fontSize: 16 },
                }}
              />
            ) : (
              <Victory.VictoryAxis
                dependentAxis
                tickCount={6}
                tickValues={[0, 16, 32, 48, 64, 80]}
                crossAxis={false}
                style={{
                  grid: {
                    stroke: ({ tick }) => (tick === -10 ? 'transparent' : '#ECECEC'),
                    strokeWidth: 1,
                  },
                  axis: { stroke: '#ECECEC', strokeWidth: 0 },
                  ticks: { strokeWidth: 0 },
                  tickLabels: {
                    fill: '#000',
                    fontWeight: 500,
                    fontSize: 16,
                  },
                }}
              />
            )}

            {selectedTab !== 1 && (
              <Victory.VictoryArea
                data={data}
                x="date"
                y="value"
                interpolation="linear"
                style={{
                  data: {
                    fill: 'url(#lineAreaGradient)',
                    strokeWidth: 0,
                  },
                }}
              />
            )}
            {selectedTab === 2 && fatReadings && fatReadings?.length > 0 && (
              <Victory.VictoryScatter
                data={[{ value: scaleReadingsSummary.fatHighRange, date: fatReadings[0].date }]}
                x="date"
                y="value"
                size={0} // invisible dot
                labels={() => 'Recommended Range'}
                style={{
                  data: { stroke: theme.colors.charts.inRange, strokeWidth: 0, fill: theme.colors.charts.inRange },
                }}
                labelComponent={
                  <Victory.VictoryLabel
                    textAnchor="start"
                    verticalAnchor="end" // ✅ anchor bottom of label to the point
                    dx={6}
                    dy={-18} // ✅ small upward nudge
                    backgroundPadding={{ top: 6, bottom: 6, left: 10, right: 16 }}
                    backgroundStyle={{
                      fill: 'white',
                      stroke: theme.colors.charts.inRange,
                      strokeWidth: 1,
                    }}
                    style={{
                      fill: theme.colors.charts.inRange,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  />
                }
                standalone={false}
              />
            )}
            {selectedTab === 2 && fatReadings && fatReadings?.length > 0 && (
              <Victory.VictoryLine
                data={[
                  { value: scaleReadingsSummary.fatLowRange, date: fatReadings[0].date },
                  {
                    value: scaleReadingsSummary.fatLowRange,
                    date: fatReadings[fatReadings.length - 1].date,
                  },
                ]}
                x="date"
                y="value"
                style={{
                  data: {
                    stroke: theme.colors.charts.inRange,
                    strokeWidth: 1.5,
                  },
                }}
                standalone={false}
                interpolation="monotoneX"
              />
            )}
            {selectedTab === 2 && fatReadings && fatReadings?.length > 0 && (
              <Victory.VictoryLine
                data={[
                  { value: scaleReadingsSummary.fatHighRange, date: fatReadings[0].date },
                  {
                    value: scaleReadingsSummary.fatHighRange,
                    date: fatReadings[fatReadings.length - 1].date,
                  },
                ]}
                x="date"
                y="value"
                style={{
                  data: {
                    stroke: theme.colors.charts.inRange,
                    strokeWidth: 1.5,
                  },
                }}
                standalone={false}
                interpolation="monotoneX"
              />
            )}
            {selectedTab === 3 && muscleReadings && muscleReadings?.length > 0 && (
              <Victory.VictoryScatter
                data={[{ value: scaleReadingsSummary.muscleHighRange, date: muscleReadings[0].date }]}
                x="date"
                y="value"
                size={0} // invisible dot
                labels={() => 'Recommended Range'}
                style={{
                  data: { stroke: theme.colors.charts.inRange, strokeWidth: 0, fill: theme.colors.charts.inRange },
                }}
                labelComponent={
                  <Victory.VictoryLabel
                    textAnchor="start"
                    verticalAnchor="end" // ✅ anchor bottom of label to the point
                    dx={6}
                    dy={-18} // ✅ small upward nudge
                    backgroundPadding={{ top: 6, bottom: 6, left: 10, right: 16 }}
                    backgroundStyle={{
                      fill: 'white',
                      stroke: theme.colors.charts.inRange,
                      strokeWidth: 1,
                    }}
                    style={{
                      fill: theme.colors.charts.inRange,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  />
                }
                standalone={false}
              />
            )}
            {selectedTab === 3 && muscleReadings && muscleReadings?.length > 0 && (
              <Victory.VictoryLine
                data={[
                  { value: scaleReadingsSummary.muscleHighRange, date: muscleReadings[0].date },
                  {
                    value: scaleReadingsSummary.muscleHighRange,
                    date: muscleReadings[muscleReadings.length - 1].date,
                  },
                ]}
                x="date"
                y="value"
                style={{
                  data: {
                    stroke: theme.colors.charts.inRange,
                    strokeWidth: 1.5,
                  },
                }}
                standalone={false}
                interpolation="monotoneX"
              />
            )}
            {selectedTab === 3 && muscleReadings && muscleReadings?.length > 0 && (
              <Victory.VictoryLine
                data={[
                  { value: scaleReadingsSummary.muscleLowRange, date: muscleReadings[0].date },
                  {
                    value: scaleReadingsSummary.muscleLowRange,
                    date: muscleReadings[muscleReadings.length - 1].date,
                  },
                ]}
                x="date"
                y="value"
                style={{
                  data: {
                    stroke: theme.colors.charts.inRange,
                    strokeWidth: 1.5,
                  },
                }}
                standalone={false}
                interpolation="monotoneX"
              />
            )}
            {(selectedTab === 1 || selectedTab === 3) && (
              <Victory.VictoryLine
                data={muscleReadings}
                x="date"
                y="value"
                interpolation="linear"
                style={{
                  data: { stroke: theme.colors.charts.inRange },
                }}
                categories={{ x: orderedMuscleDates! }}
              />
            )}

            {(selectedTab === 1 || selectedTab === 3) && (
              <Victory.VictoryScatter
                data={muscleReadings}
                x="date"
                y="value"
                symbol={'square'}
                style={{
                  data: { stroke: theme.colors.charts.inRange, strokeWidth: 1.5, fill: theme.colors.charts.inRange, zIndex: 1000 },
                }}
                categories={{ x: orderedMuscleDates! }}
                size={5}
                labels={(d) =>
                  selectedTab === 1
                    ? [
                        `Muscle - ${muscleReadings?.find((m) => m.date === d.datum.date)?.value}%`,
                        `Fat - ${fatReadings?.find((m) => m.date === d.datum.date)?.value}%`,
                        `Water - ${waterReadings?.find((m) => m.date === d.datum.date)?.value}%`,
                      ]
                    : [`${d.datum.value}%`, dayjs(d.datum.date).format('MM/DD/YY'), dayjs(d.datum.date).format('hh:mm A')]
                }
                labelComponent={
                  <Victory.VictoryTooltip
                    cornerRadius={4}
                    flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
                    flyoutStyle={{
                      fill: 'white',
                      stroke: theme.colors.primary1,
                      strokeWidth: 1.8,
                    }}
                    labelComponent={
                      <Victory.VictoryLabel
                        textAnchor="start"
                        lineHeight={1.5}
                        dy={2}
                        dx={selectedTab === 1 ? -50 : -30}
                        style={[
                          { fontWeight: 'bold', fontFamily: 'raleway' },
                          { fontSize: 14, fontFamily: 'raleway' },
                          { fontSize: 14, fontFamily: 'raleway' },
                        ]}
                      />
                    }
                  />
                }
              />
            )}
            {selectedTab === 4 && waterReadings && waterReadings?.length > 0 && (
              <Victory.VictoryScatter
                data={[{ value: 65, date: waterReadings[0].date }]}
                x="date"
                y="value"
                size={0} // invisible dot
                labels={() => 'Recommended Range'}
                style={{
                  data: { stroke: theme.colors.charts.inRange, strokeWidth: 0, fill: theme.colors.charts.inRange },
                }}
                labelComponent={
                  <Victory.VictoryLabel
                    textAnchor="start"
                    verticalAnchor="end" // ✅ anchor bottom of label to the point
                    dx={6}
                    dy={-18} // ✅ small upward nudge
                    backgroundPadding={{ top: 6, bottom: 6, left: 10, right: 16 }}
                    backgroundStyle={{
                      fill: 'white',
                      stroke: theme.colors.charts.inRange,
                      strokeWidth: 1,
                    }}
                    style={{
                      fill: theme.colors.charts.inRange,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  />
                }
                standalone={false}
              />
            )}

            {selectedTab === 4 && waterReadings && waterReadings?.length > 0 && (
              <Victory.VictoryLine
                data={[
                  { value: 65, date: waterReadings[0].date },
                  {
                    value: 65,
                    date: waterReadings[waterReadings.length - 1].date,
                  },
                ]}
                x="date"
                y="value"
                style={{
                  data: {
                    stroke: theme.colors.charts.inRange,
                    strokeWidth: 1.5,
                  },
                }}
                standalone={false}
                interpolation="monotoneX"
              />
            )}

            {selectedTab === 4 && waterReadings && waterReadings?.length > 0 && (
              <Victory.VictoryLine
                data={[
                  { value: 50, date: waterReadings[0].date },
                  {
                    value: 50,
                    date: waterReadings[waterReadings.length - 1].date,
                  },
                ]}
                x="date"
                y="value"
                style={{
                  data: {
                    stroke: theme.colors.charts.inRange,
                    strokeWidth: 1.5,
                  },
                }}
                standalone={false}
                interpolation="monotoneX"
              />
            )}

            {(selectedTab === 1 || selectedTab === 4) && (
              <Victory.VictoryLine
                data={waterReadings}
                categories={{ x: orderedWaterDates! }}
                x="date"
                y="value"
                interpolation="linear"
                style={{
                  data: { stroke: '#0092BC' },
                }}
              />
            )}

            {(selectedTab === 1 || selectedTab === 4) && (
              <Victory.VictoryScatter
                data={waterReadings}
                x="date"
                y="value"
                symbol={'triangleUp'}
                style={{
                  data: { stroke: '#0092BC', strokeWidth: 1.5, fill: '#0092BC' },
                }}
                categories={{ x: orderedWaterDates! }}
                size={5}
                labels={(d) =>
                  selectedTab === 1
                    ? [
                        `Water - ${waterReadings?.find((m) => m.date === d.datum.date)?.value}%`,
                        `Muscle - ${muscleReadings?.find((m) => m.date === d.datum.date)?.value}%`,
                        `Fat - ${fatReadings?.find((m) => m.date === d.datum.date)?.value}%`,
                      ]
                    : [`${d.datum.value}%`, dayjs(d.datum.date).format('MM/DD/YY'), dayjs(d.datum.date).format('hh:mm A')]
                }
                labelComponent={
                  <Victory.VictoryTooltip
                    cornerRadius={4}
                    flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
                    flyoutStyle={{
                      fill: 'white',
                      stroke: theme.colors.primary1,
                      strokeWidth: 1.8,
                    }}
                    labelComponent={
                      <Victory.VictoryLabel
                        textAnchor="start"
                        lineHeight={1.5}
                        dy={2}
                        dx={selectedTab === 1 ? -50 : -30}
                        style={[
                          { fontWeight: 'bold', fontFamily: 'raleway' }, // Fat

                          { fontSize: 14, fontFamily: 'raleway' },
                          { fontSize: 14, fontFamily: 'raleway' },
                        ]}
                      />
                    }
                  />
                }
              />
            )}

            {/* 3) Line on top */}
            {selectedTab === 0 && (
              <Victory.VictoryLine
                data={chartReadings}
                x="date"
                y="value"
                interpolation="linear"
                style={{ data: { stroke: theme.colors.primary1, strokeWidth: 1.5 } }}
              />
            )}
            {selectedTab === 0 && (
              <Victory.VictoryScatter
                data={chartReadings}
                size={5}
                x="date"
                y="value"
                style={{
                  data: {
                    stroke: theme.colors.primary1,
                    strokeWidth: 1.5,
                    fill: theme.colors.primary1,
                  },
                }}
                labels={(d) => `${d.datum.value}\n${d.datum.date}`}
                labelComponent={
                  <Victory.VictoryTooltip
                    cornerRadius={4}
                    flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
                    style={{ fontFamily: 'raleway' }}
                    flyoutStyle={{
                      fill: 'white',
                      stroke: theme.colors.primary1,
                      strokeWidth: 1.5,
                      fontFamily: 'raleway',
                    }}
                  />
                }
              />
            )}
            {(selectedTab === 1 || selectedTab === 2) && (
              <Victory.VictoryLine
                data={fatReadings}
                categories={{ x: orderedFatDates! }}
                x="date"
                y="value"
                interpolation="linear"
                style={{
                  data: { stroke: '#253746' },
                }}
              />
            )}
            {(selectedTab === 1 || selectedTab === 2) && (
              <Victory.VictoryScatter
                data={fatReadings}
                x="date"
                y="value"
                style={{
                  data: { stroke: '#253746', strokeWidth: 1.5, fill: '#253746' },
                }}
                categories={{ x: orderedFatDates! }}
                size={5}
                labels={(d) =>
                  selectedTab === 1
                    ? [
                        `Fat - ${fatReadings?.find((m) => m.date === d.datum.date)?.value}%`,
                        `Muscle - ${muscleReadings?.find((m) => m.date === d.datum.date)?.value}%`,
                        `Water - ${waterReadings?.find((m) => m.date === d.datum.date)?.value}%`,
                      ]
                    : [`${d.datum.value}%`, dayjs(d.datum.date).format('MM/DD/YY'), dayjs(d.datum.date).format('hh:mm A')]
                }
                labelComponent={
                  <Victory.VictoryTooltip
                    cornerRadius={4}
                    flyoutPadding={{ top: 8, bottom: 8, left: 12, right: 12 }}
                    flyoutStyle={{
                      fill: 'white',
                      stroke: theme.colors.primary1,
                      strokeWidth: 1.8,
                    }}
                    labelComponent={
                      <Victory.VictoryLabel
                        textAnchor="start"
                        lineHeight={1.5}
                        dy={2}
                        dx={selectedTab === 1 ? -50 : -30}
                        style={[
                          { fontWeight: 'bold', fontFamily: 'raleway' }, // Fat

                          { fontSize: 14, fontFamily: 'raleway' },
                          { fontSize: 14, fontFamily: 'raleway' },
                        ]}
                      />
                    }
                  />
                }
              />
            )}
          </Victory.VictoryChart>
        </svg>
      </div>
    </div>
  )
}
